import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import DeleteIcon from '@/assets/icons/delete.svg?react'
import PauseIcon from '@/assets/icons/pause.svg?react'
import ResumeIcon from '@/assets/icons/resume.svg?react'
import TransferIcon from '@/assets/icons/transfer.svg?react'
import { EmptyView, Trans } from '@/components'
import { formatBytes, formatSpeed } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { FILE_STATUS, IUploadTask } from '../core/hooks/useUpload'
import { IDownloadTask } from '../core/types'
import { FileIcon } from './FileIcon'

interface TransferStatusIndicatorProps {
  uploadTasks: IUploadTask[]
  downloadTasks: IDownloadTask[]
  onPauseUpload?: (task: IUploadTask) => void
  onResumeUpload?: (task: IUploadTask) => void
  onCancelDownload?: (taskId: string) => void
  onRetryDownload?: (task: IDownloadTask) => void
  onRemoveUpload?: (task: IUploadTask) => void
}

export function TransferStatusIndicator({
  uploadTasks,
  downloadTasks,
  onPauseUpload,
  onResumeUpload,
  onCancelDownload,
  onRetryDownload,
  onRemoveUpload,
}: TransferStatusIndicatorProps) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'download' | 'upload'>('download')

  // Filter active tasks for badge count
  const activeUploads = uploadTasks.filter(
    (task) =>
      task.status === FILE_STATUS.UPLOADING ||
      task.status === FILE_STATUS.PAUSED ||
      task.status === FILE_STATUS.PENDING,
  )
  const activeDownloads = downloadTasks.filter(
    (task) => task.status === 'downloading' || task.status === 'pending',
  )

  const totalActiveTasks = activeUploads.length + activeDownloads.length

  // Get file extension from name
  const getFileExt = (name: string) => {
    const parts = name.split('.')
    return parts.length > 1 ? parts.pop() : undefined
  }

  // Upload status with color
  const getUploadStatus = (task: IUploadTask): { text: string; color: string } => {
    const percentage =
      task.loaded && task.size > 0 ? Math.min(100, (100 * task.loaded) / task.size).toFixed(0) : '0'

    switch (task.status) {
      case FILE_STATUS.UPLOADING: {
        const speed = task.speed > 0 ? formatSpeed(task.speed) : ''
        return {
          text: speed ? `${speed}  ${percentage}%` : `${percentage}%`,
          color: 'text-tertiary',
        }
      }
      case FILE_STATUS.PAUSED:
        return { text: `${t('data.transfer.paused')}  ${percentage}%`, color: 'text-tertiary' }
      case FILE_STATUS.PENDING:
        return { text: t('data.transfer.starting'), color: 'text-tertiary' }
      case FILE_STATUS.SUCCESS:
        return {
          text: `${t('data.transfer.done')}  ${formatBytes(task.size)}`,
          color: 'text-tertiary',
        }
      case FILE_STATUS.FAILED:
        return { text: t('data.transfer.error'), color: 'text-error' }
      default:
        return { text: t('data.transfer.starting'), color: 'text-tertiary' }
    }
  }

  // Download status with color
  const getDownloadStatus = (task: IDownloadTask): { text: string; color: string } => {
    const percentage =
      task.loaded && task.size > 0 ? Math.min(100, (100 * task.loaded) / task.size).toFixed(0) : '0'

    switch (task.status) {
      case 'downloading': {
        const speed = task.speed > 0 ? formatSpeed(task.speed) : ''
        return {
          text: speed ? `${speed}  ${percentage}%` : `${percentage}%`,
          color: 'text-tertiary',
        }
      }
      case 'pending':
        return { text: t('data.transfer.starting'), color: 'text-tertiary' }
      case 'completed':
        return {
          text: `${t('data.transfer.done')}  ${formatBytes(task.size)}`,
          color: 'text-tertiary',
        }
      case 'error':
        return { text: t('data.transfer.error'), color: 'text-error' }
      case 'cancelled':
        return { text: t('data.transfer.cancelled'), color: 'text-tertiary' }
      default:
        return { text: t('data.transfer.starting'), color: 'text-tertiary' }
    }
  }

  return (
    <div className="relative group">
      <div className="bg-neutral-50 p-2 rounded-lg relative cursor-pointer">
        <TransferIcon className="w-5 h-5" />
        {totalActiveTasks > 0 && (
          <div className="absolute -top-1 -right-1 bg-error text-white rounded-full min-w-4 h-4 px-1 flex items-center justify-center t-x-sb">
            {totalActiveTasks > 99 ? '99+' : totalActiveTasks}
          </div>
        )}
      </div>

      {/* Dropdown Panel */}
      <div className="absolute left-0 top-full hidden group-hover:block pt-2 z-50">
        <div className="bg-panel-white shadow-card-100 rounded-xl py-4 min-w-[320px] max-h-[400px] overflow-hidden flex flex-col">
          {/* Tab Header */}
          <div className="flex gap-2 px-4 h-9">
            <button
              className={cn(
                't-x-sb flex items-center px-2',
                activeTab === 'download' ? 'text-primary' : 'text-tertiary',
              )}
              onClick={() => setActiveTab('download')}
            >
              <Trans i18nKey="data.transfer.download">Download</Trans>
              <span className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-neutral-50">
                {downloadTasks.length}
              </span>
            </button>
            <button
              className={cn(
                't-x-sb flex items-center px-2',
                activeTab === 'upload' ? 'text-primary' : 'text-tertiary',
              )}
              onClick={() => setActiveTab('upload')}
            >
              <Trans i18nKey="data.transfer.upload">Upload</Trans>
              <span className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-neutral-50">
                {uploadTasks.length}
              </span>
            </button>
          </div>

          {/* Task List */}
          <div className="flex-1 overflow-y-auto pt-2">
            {activeTab === 'download' ? (
              // Download Tasks
              downloadTasks.length === 0 ? (
                <EmptyView
                  title={
                    <span className="t-sm text-secondary">
                      <Trans i18nKey="common.emptyViewNoDataMsg" />
                    </span>
                  }
                />
              ) : (
                downloadTasks.map((task) => {
                  const status = getDownloadStatus(task)
                  return (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 h-14 px-4 hover:bg-panel-gray-100"
                    >
                      <FileIcon ext={getFileExt(task.name)} className="shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="t-x-sb truncate" title={task.name}>
                          {task.name}
                        </div>
                        <div className={cn('t-xs', status.color)}>{status.text}</div>
                      </div>
                      <div className="flex-shrink-0">
                        {task.status === 'downloading' && onCancelDownload && (
                          <button
                            className="p-1 rounded hover:bg-panel-gray-200"
                            onClick={() => onCancelDownload(task.id)}
                            title="Cancel"
                          >
                            <PauseIcon className="w-4 h-4 text-secondary" />
                          </button>
                        )}
                        {task.status === 'completed' && (
                          <button
                            className="p-1 rounded hover:bg-panel-gray-200"
                            onClick={() => onCancelDownload?.(task.id)}
                            title="Remove"
                          >
                            <DeleteIcon className="w-4 h-4 text-secondary" />
                          </button>
                        )}
                        {task.status === 'error' && onRetryDownload && (
                          <button
                            className="p-1 rounded hover:bg-panel-gray-200"
                            onClick={() => onRetryDownload(task)}
                            title="Retry"
                          >
                            <ResumeIcon className="w-4 h-4 text-secondary" />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })
              )
            ) : // Upload Tasks
            uploadTasks.length === 0 ? (
              <EmptyView
                title={
                  <span className="t-sm text-secondary">
                    <Trans i18nKey="common.emptyViewNoDataMsg" />
                  </span>
                }
              />
            ) : (
              uploadTasks.map((task, idx) => {
                const status = getUploadStatus(task)
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-3 h-14 px-4 hover:bg-panel-gray-100"
                  >
                    <FileIcon ext={getFileExt(task.name)} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="t-x-sb truncate" title={task.name}>
                        {task.name}
                      </div>
                      <div className={cn('t-xs', status.color)}>{status.text}</div>
                    </div>
                    <div className="flex-shrink-0">
                      {task.status === FILE_STATUS.UPLOADING && task.client && onPauseUpload && (
                        <button
                          className="p-1 rounded hover:bg-panel-gray-200"
                          onClick={() => onPauseUpload(task)}
                          title="Pause"
                        >
                          <PauseIcon className="w-4 h-4 text-secondary" />
                        </button>
                      )}
                      {task.status === FILE_STATUS.PAUSED && task.client && onResumeUpload && (
                        <button
                          className="p-1 rounded hover:bg-panel-gray-200"
                          onClick={() => onResumeUpload(task)}
                          title="Resume"
                        >
                          <ResumeIcon className="w-4 h-4 text-secondary" />
                        </button>
                      )}
                      {task.status === FILE_STATUS.SUCCESS && onRemoveUpload && (
                        <button
                          className="p-1 rounded hover:bg-panel-gray-200"
                          onClick={() => onRemoveUpload(task)}
                          title="Remove"
                        >
                          <DeleteIcon className="w-4 h-4 text-secondary" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
